import "leaflet/dist/leaflet.css";

import {
    Box,
    Container,
    Dialog,
    Drawer,
    Fab,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import {
    DRAWER_WIDTH,
    DYNAMIC_FEATURE_ID_FIELD_NAME,
    DYNAMIC_FEATURE_X_Y_FIELD_NAME,
    GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES,
    MAP_TILE_LAYERS,
} from "./constants";
import { FormProvider, useForm } from "react-hook-form";
import {
    PARCELLAIRE_REDUCER_ACTIONS,
    initialParcellaireState,
    parcellaireReducer,
} from "./parcellaireReducer";
import { fetchWMSLayerBounds, getSelectableMetadata } from "./utils";
import { formatDateISO, offset } from "../../utils/formatTime";
import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import { useLiteralRequests, useMissionGeometries } from "./hooks";
import { useParams, useSearchParams } from "react-router-dom";

import { APPBAR_HEIGHT } from "../../layouts/orders/constants";
import AddIcon from "@mui/icons-material/Add";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { BACKEND_ROUTES } from "../../backendRoutes";
import BorderVerticalIcon from "@mui/icons-material/BorderVertical";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import GrassIcon from "@mui/icons-material/Grass";
import LastPageIcon from "@mui/icons-material/LastPage";
import { MapContainer } from "react-leaflet";
import { MapLayout } from "./MapLayout";
import { PAGINATION } from "../../constants";
import { ParcellaireAddProperty } from "./ParcellaireAddProperty";
import { ParcellaireCreationContainer } from "./ParcellaireCreationContainer";
import { ParcellaireCreationFormNoImport } from "./ParcellaireCreationFormNoImport";
import { ParcellaireHCCSaveModal } from "./ParcellaireHCCSaveModal";
import { ParcellaireHeader } from "./ParcellaireHeader";
import { ParcellaireMetadataForm } from "./ParcellaireMetadataForm";
import { ParcellaireOrthoForm } from "./ParcellaireOrthoForm";
import { ParcellaireScalePlots } from "./ParcellaireScalePlots";
import { replaceMapDataGeoJson } from "./utils/fileIoUtils";
import { useCrops } from "../../hooks";
import { useFetch } from "../../hooks";
import useSWR from "swr";
import { useSnackbar } from "../../hooks";

export function Parcellaire({ isLiteral }) {
    const [map, setMap] = useState();
    const [plotColor, setPlotColor] = useState("#3388ff");
    const [openDrawer, setOpenDrawer] = useState(isLiteral);
    const [isHCCSaveOpen, setIsHCCSaveOpen] = useState(false);
    const [openExportForm, setOpenExportForm] = useState(false);
    const [openOrthoForm, setOpenOrthoForm] = useState(false);
    const [openAddPropertyDialog, setOpenAddPropertyDialog] = useState(false);
    const [openScaleDialog, setOpenScaleDialog] = useState(false);
    const [orthoimageName, setOrthoimageName] = useState("plain");
    const [coordMarkerMode, setCoordMarkerMode] = useState(false);
    const [isRulerActive, setIsRulerActive] = useState(false);
    const [blueRectangleData, setBlueRectangleData] = useState(null);
    const [selectableMetadata, setSelectableMetadata] = useState([]);
    const [geoJsonKey, setGeoJsonKey] = useState(new Date().getTime());
    const [selectedTileLayer, setSelectedTileLayer] = useState(
        isLiteral ? MAP_TILE_LAYERS.OPEN_STREET_MAP : MAP_TILE_LAYERS.SATELLITE
    );
    const [displayToggles, setFirstToggles] = useState([]);
    const [transformationToggles, setSecondToggles] = useState([
        GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.LOCKED_SHAPE_MODE,
    ]);
    const [parcellaireState, parcellaireDispatch] = useReducer(
        parcellaireReducer,
        initialParcellaireState
    );
    const onSoilDrawRef = useRef(null);
    const [isDrawingSoil, setIsDrawingSoil] = useState(false);
    const soilPolygonsRef = useRef(null);

    const onRadioMetricTargetDrawRef = useRef(null);
    const [isDrawingRadioMetricTarget, setIsDrawingRadioMetricTarget] =
        useState(false);
    const radioMetricTargetPolygonsRef = useRef(null);

    // Timer state for tracking editing time
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const intervalIdRef = useRef(null);

    const methods = useForm({
        defaultValues: getParcellaireFormDefaultValues(isLiteral),
    });

    const [
        { error: siteError },
        { data: literalPipelineTemplates, error: pipelineTemplatesError },
    ] = useLiteralRequests();

    const { missionUuid: initialMissionUuid } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const siteUuid = searchParams.get("siteUuid");
    const orthoMissionUuid = searchParams.get("missionUuid");
    const fastOrthoimage = searchParams.get("fastOrthoimage") === "true";
    const { data: crops, error: cropsFetchError } = useCrops();

    const { data: mission, error: missionFetchError } = useSWR(
        initialMissionUuid
            ? `${BACKEND_ROUTES.MISSION}/${initialMissionUuid}`
            : null
    );

    const { data: site, error: siteFetchError } = useSWR(
        mission ? `${BACKEND_ROUTES.SITE}/${mission.siteUuid}` : null
    );

    const { data: orthoimageSite, error: orthoimageSiteFetchError } = useSWR(
        siteUuid ? `${BACKEND_ROUTES.SITE}/${siteUuid}` : null
    );

    const { data: orthoimageMission, error: orthoimageMissionFetchError } =
        useSWR(
            orthoMissionUuid
                ? `${BACKEND_ROUTES.MISSION}/${orthoMissionUuid}`
                : null
        );

    const { geometries: missionGeometries, refetch: refetchMissionGeometries } =
        useMissionGeometries(orthoMissionUuid);

    const { data: contract, error: contractFetchError } = useSWR(
        site ? `${BACKEND_ROUTES.CONTRACT}/${site.contractUuid}` : null
    );

    const { data: orthoimageContract, error: orthoimageContractFetchError } =
        useSWR(
            orthoimageSite
                ? `${BACKEND_ROUTES.CONTRACT}/${orthoimageSite.contractUuid}`
                : null
        );

    const { data: company, error: companyFetchError } = useSWR(
        contract ? `${BACKEND_ROUTES.COMPANY}/${contract.companyUuid}` : null
    );

    const { data: orthoimageCompany, error: orthoimageCompanyFetchError } =
        useSWR(
            orthoimageContract
                ? `${BACKEND_ROUTES.COMPANY}/${orthoimageContract.companyUuid}`
                : null
        );

    const { get } = useFetch();
    const { openSnackbar } = useSnackbar();

    const fetchAndLoadParcellaire = useCallback(async () => {
        try {
            const featuresForParcellaire = await get(
                `${BACKEND_ROUTES.MISSION}/${initialMissionUuid}/geojson-features?&limit=${PAGINATION.MISSION_FEATURE.LIMIT.MAX}`,
                {
                    forwardError: true,
                }
            );

            // Fetch all pages if not already the case
            if (
                PAGINATION.MISSION_FEATURE.LIMIT.MAX <
                featuresForParcellaire.count
            ) {
                const promises = [];
                for (
                    let step = PAGINATION.MISSION_FEATURE.LIMIT.MAX;
                    step < featuresForParcellaire.count;
                    step += PAGINATION.MISSION_FEATURE.LIMIT.MAX
                ) {
                    promises.push(
                        get(
                            `${BACKEND_ROUTES.MISSION}/${initialMissionUuid}/geojson-features?offset=${step}&limit=${PAGINATION.MISSION_FEATURE.LIMIT.MAX}`,
                            {
                                forwardError: true,
                            }
                        )
                    );
                }

                const allFeatureResults = await Promise.all(promises);

                featuresForParcellaire.rows.concat(
                    allFeatureResults.flatMap((result) => result.rows)
                );
            }

            const parcellaire = {
                type: "FeatureCollection",
                features: featuresForParcellaire.rows.map((feature, index) => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        [DYNAMIC_FEATURE_ID_FIELD_NAME]: `${index}`,
                    },
                })),
            };
            replaceMapDataGeoJson(
                parcellaireDispatch,
                parcellaire,
                map,
                initialMissionUuid
            );
            setGeoJsonKey(new Date().getTime());
            const availableMetadata = [];
            parcellaire.features.forEach(({ properties = {} }) => {
                availableMetadata.push(
                    ...Object.keys(properties).filter(
                        (property) => property !== DYNAMIC_FEATURE_ID_FIELD_NAME
                    )
                );
            });
            setSelectableMetadata(
                getSelectableMetadata([...new Set(availableMetadata)])
            );
        } catch (error) {
            openSnackbar(
                "Something went wrong. Please try again later.",
                "error"
            );
        }
    }, [get, initialMissionUuid, map, openSnackbar]);

    useEffect(() => {
        if (!orthoimageSite) return;
        parcellaireDispatch({
            type: PARCELLAIRE_REDUCER_ACTIONS.METADATA,
            metadata: {
                site: {
                    id: orthoimageSite.cloverfieldUuid,
                    uuid: orthoimageSite.uuid,
                },
                nameSpace: fastOrthoimage === false ? "cloverfield" : "hcc",
                source: "select",
            },
        });
        setGeoJsonKey(new Date().getTime());
    }, [fastOrthoimage, orthoimageSite]);

    const orthoimageKey = useMemo(() => {
        // url is either hcc:[MISSION_UUID]-[NAME] or cloverfield:[SITE_ID]-[MISSION_DATE]-[NAME]
        // if nameSpace is cloverfield, we have two potential sources (select or imported file)
        // if it's select, mission date is in orthoimageMission, but if it's imported file, mission date is in parcellaireState.metadata
        return parcellaireState.metadata
            ? parcellaireState.metadata.nameSpace === "hcc"
                ? `hcc:${orthoimageMission?.uuid}-${orthoimageName}`
                : `cloverfield:${parcellaireState.metadata.site.id}-${parcellaireState.metadata.source === "select" && orthoimageMission ? formatDateISO(offset(new Date(orthoimageMission.date), true)) : parcellaireState.metadata.date}-${orthoimageName}`
            : null;
    }, [orthoimageMission, orthoimageName, parcellaireState.metadata]);

    const zoomToOrthoimage = useCallback(async () => {
        if (!orthoimageKey || !map) return;
        try {
            const bounds = await fetchWMSLayerBounds(orthoimageKey, get);
            map.fitBounds(bounds, { maxZoom: 19 });
        } catch (error) {
            openSnackbar("Could not zoom to orthoimage bounds.", "error");
        }
    }, [orthoimageKey, map, openSnackbar, get]);

    useEffect(() => {
        if (orthoimageKey) {
            zoomToOrthoimage();
        }
    }, [orthoimageKey, zoomToOrthoimage]);

    useEffect(() => {
        if (initialMissionUuid && mission) {
            fetchAndLoadParcellaire();
        }
    }, [fetchAndLoadParcellaire, initialMissionUuid, mission]);

    // Timer effect: increment every second and handle window focus/blur
    // Timer only runs when a plotmap is displayed (transformedGeoJson exists)
    useEffect(() => {
        const startTimer = () => {
            if (intervalIdRef.current) return; // Already running
            intervalIdRef.current = setInterval(() => {
                setElapsedSeconds((prev) => prev + 1);
            }, 1000);
        };

        const stopTimer = () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        };

        const handleFocus = () => {
            // Only start timer if plotmap is displayed
            if (parcellaireState.transformedGeoJson) {
                startTimer();
            }
        };

        const handleBlur = () => {
            stopTimer();
        };

        // Only start timer if plotmap is displayed
        if (parcellaireState.transformedGeoJson) {
            startTimer();
            // Add event listeners for focus/blur
            window.addEventListener("focus", handleFocus);
            window.addEventListener("blur", handleBlur);
        } else {
            stopTimer();
        }

        // Cleanup on unmount or when dependencies change
        return () => {
            stopTimer();
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("blur", handleBlur);
        };
    }, [parcellaireState.transformedGeoJson]);

    const error =
        siteError ??
        siteFetchError ??
        orthoimageSiteFetchError ??
        missionFetchError ??
        orthoimageMissionFetchError ??
        contractFetchError ??
        orthoimageContractFetchError ??
        companyFetchError ??
        orthoimageCompanyFetchError ??
        cropsFetchError ??
        pipelineTemplatesError;

    if (error) return <FetchErrorAlert error={error} />;

    // If `literalPipelineTemplates` is defined then both site and pipeline templates fetches are successful
    if (
        (isLiteral && !literalPipelineTemplates) ||
        !crops ||
        (initialMissionUuid && !site)
    ) {
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="100vh"
            />
        );
    }

    return (
        <FormProvider {...methods}>
            <Stack direction="column">
                <ParcellaireHeader
                    blueRectangleData={blueRectangleData}
                    displayToggles={displayToggles}
                    elapsedSeconds={elapsedSeconds}
                    isLiteral={isLiteral}
                    map={map}
                    orthoimageKey={orthoimageKey}
                    orthoimageName={orthoimageName}
                    openDrawer={openDrawer}
                    parcellaireDispatch={parcellaireDispatch}
                    parcellaireState={parcellaireState}
                    plotColor={plotColor}
                    transformationToggles={transformationToggles}
                    selectableMetadata={selectableMetadata}
                    selectedTileLayer={selectedTileLayer}
                    setBlueRectangleData={setBlueRectangleData}
                    setFirstToggles={setFirstToggles}
                    setGeoJsonKey={setGeoJsonKey}
                    setIsHCCSaveOpen={setIsHCCSaveOpen}
                    setOpenExportForm={setOpenExportForm}
                    setOpenOrthoForm={setOpenOrthoForm}
                    setOrthoimageName={setOrthoimageName}
                    setPlotColor={setPlotColor}
                    setSecondToggles={setSecondToggles}
                    setSelectableMetadata={setSelectableMetadata}
                    setSelectedTileLayer={setSelectedTileLayer}
                    soilPolygonsRef={soilPolygonsRef}
                    radioMetricTargetPolygonRef={radioMetricTargetPolygonsRef}
                    isRulerActive={isRulerActive}
                    setIsRulerActive={setIsRulerActive}
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    missionGeometries={missionGeometries}
                    refetchMissionGeometries={refetchMissionGeometries}
                    orthoimageCompany={orthoimageCompany}
                    orthoimageContract={orthoimageContract}
                    orthoimageSite={orthoimageSite}
                    orthoimageMission={orthoimageMission}
                />
                <Box
                    id="map"
                    width="100%"
                    height={`calc(100vh - ${APPBAR_HEIGHT}px)`}
                >
                    <MapContainer
                        attributionControl={false}
                        center={[26.0, 30.0]}
                        maxZoom={25}
                        minZoom={3}
                        zoom={3}
                        maxBounds={[
                            [-90, -270],
                            [90, 270],
                        ]}
                        maxBoundsViscosity={0.8}
                        style={{
                            width: "100%",
                            height: "100%",
                            backgroundImage: "url(/static/mapbg.png)",
                            backgroundSize: "1300px",
                        }}
                        ref={setMap}
                    >
                        <MapLayout
                            blueRectangleData={blueRectangleData}
                            displayToggles={displayToggles}
                            geoJsonKey={geoJsonKey}
                            isLiteral={isLiteral}
                            orthoimageKey={orthoimageKey}
                            zoomToOrthoimage={zoomToOrthoimage}
                            parcellaireDispatch={parcellaireDispatch}
                            parcellaireState={parcellaireState}
                            plotColor={plotColor}
                            transformationToggles={transformationToggles}
                            selectedTileLayer={selectedTileLayer}
                            setCoordMarkerMode={setCoordMarkerMode}
                            setGeoJsonKey={setGeoJsonKey}
                            onSoilDrawRef={onSoilDrawRef}
                            setIsDrawingSoil={setIsDrawingSoil}
                            soilPolygonsRef={soilPolygonsRef}
                            onRadioMetricTargetDrawRef={
                                onRadioMetricTargetDrawRef
                            }
                            setIsDrawingRadioMetricTarget={
                                setIsDrawingRadioMetricTarget
                            }
                            radioMetricPolygonsRef={
                                radioMetricTargetPolygonsRef
                            }
                            radioMetricTargetName={
                                missionGeometries.radiometricTarget?.name
                            }
                            isRulerActive={isRulerActive}
                            setIsRulerActive={setIsRulerActive}
                            openScaleDialog={openScaleDialog}
                            setOpenScaleDialog={setOpenScaleDialog}
                        />
                    </MapContainer>
                </Box>
            </Stack>
            <Drawer
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                variant="persistent"
                PaperProps={{
                    sx: {
                        width: DRAWER_WIDTH,
                        bgcolor: "background.neutral",
                    },
                }}
                anchor="right"
            >
                <>
                    <DrawerHeader>
                        <Typography variant="h5">Plot map creation</Typography>
                    </DrawerHeader>
                    <Container
                        maxWidth="xl"
                        sx={{ bgcolor: "background.default" }}
                    >
                        {isLiteral ? (
                            <ParcellaireCreationFormNoImport
                                coordMarkerMode={coordMarkerMode}
                                isLiteral={true}
                                map={map}
                                parcellaireDispatch={parcellaireDispatch}
                                setBlueRectangleData={setBlueRectangleData}
                                setCoordMarkerMode={setCoordMarkerMode}
                                setGeoJsonKey={setGeoJsonKey}
                                setIsOpen={setOpenDrawer}
                                setSelectableMetadata={setSelectableMetadata}
                            />
                        ) : (
                            <ParcellaireCreationContainer
                                coordMarkerMode={coordMarkerMode}
                                map={map}
                                parcellaireState={parcellaireState}
                                parcellaireDispatch={parcellaireDispatch}
                                setBlueRectangleData={setBlueRectangleData}
                                setCoordMarkerMode={setCoordMarkerMode}
                                setGeoJsonKey={setGeoJsonKey}
                                setIsOpen={setOpenDrawer}
                                setSelectableMetadata={setSelectableMetadata}
                                initialCompany={company}
                                initialContract={contract}
                                initialSite={site}
                                initialMission={mission}
                                crops={crops.rows}
                                setSearchParams={setSearchParams}
                                hasInitialMission={Boolean(initialMissionUuid)}
                            />
                        )}
                    </Container>
                </>
            </Drawer>

            <Fab
                size={openDrawer ? "small" : "large"}
                color="primary"
                variant={openDrawer ? "circular" : "extended"}
                sx={(theme) => ({
                    transition: theme.transitions.create(
                        "right",
                        openDrawer
                            ? {
                                  duration:
                                      theme.transitions.duration.leavingScreen,
                                  easing: theme.transitions.easing.easeOut,
                              }
                            : {
                                  duration:
                                      theme.transitions.duration.enteringScreen,
                                  easing: theme.transitions.easing.sharp,
                              }
                    ),
                    position: "absolute",
                    right: openDrawer ? DRAWER_WIDTH - 20 : 10,
                    top: 76,
                    zIndex: theme.zIndex.drawer,
                })}
                onClick={() => setOpenDrawer(!openDrawer)}
            >
                {openDrawer ? (
                    <LastPageIcon />
                ) : (
                    <>
                        Create new plot map
                        <AddIcon />
                    </>
                )}
            </Fab>

            {!isLiteral && (
                <>
                    <Fab
                        size={openDrawer ? "small" : "large"}
                        color="primary"
                        variant={openDrawer ? "circular" : "extended"}
                        sx={(theme) => ({
                            transition: theme.transitions.create(
                                "right",
                                openDrawer
                                    ? {
                                          duration:
                                              theme.transitions.duration
                                                  .leavingScreen,
                                          easing: theme.transitions.easing
                                              .easeOut,
                                      }
                                    : {
                                          duration:
                                              theme.transitions.duration
                                                  .enteringScreen,
                                          easing: theme.transitions.easing
                                              .sharp,
                                      }
                            ),
                            position: "absolute",
                            right: openDrawer ? DRAWER_WIDTH - 20 : 10,
                            top: 146,
                            zIndex: theme.zIndex.drawer,
                        })}
                        onClick={() => {
                            if (openDrawer) {
                                setOpenDrawer(false);
                            }
                            if (onSoilDrawRef.current) {
                                if (isDrawingSoil) {
                                    onSoilDrawRef.current.disable();
                                    setIsDrawingSoil(false);
                                } else {
                                    onSoilDrawRef.current.enable();
                                    setIsDrawingSoil(true);
                                    setIsDrawingRadioMetricTarget(false);
                                }
                            }
                        }}
                    >
                        {openDrawer ? (
                            <GrassIcon />
                        ) : (
                            <>
                                {isDrawingSoil ? "Stop Drawing" : "Create Soil"}
                                <AddIcon />
                            </>
                        )}
                    </Fab>
                    <Fab
                        size={openDrawer ? "small" : "large"}
                        color="primary"
                        variant={openDrawer ? "circular" : "extended"}
                        sx={(theme) => ({
                            transition: theme.transitions.create(
                                "right",
                                openDrawer
                                    ? {
                                          duration:
                                              theme.transitions.duration
                                                  .leavingScreen,
                                          easing: theme.transitions.easing
                                              .easeOut,
                                      }
                                    : {
                                          duration:
                                              theme.transitions.duration
                                                  .enteringScreen,
                                          easing: theme.transitions.easing
                                              .sharp,
                                      }
                            ),
                            position: "absolute",
                            right: openDrawer ? DRAWER_WIDTH - 20 : 10,
                            top: 216,
                            zIndex: theme.zIndex.drawer,
                        })}
                        onClick={() => {
                            if (openDrawer) {
                                setOpenDrawer(false);
                            }
                            if (onRadioMetricTargetDrawRef.current) {
                                if (isDrawingRadioMetricTarget) {
                                    onRadioMetricTargetDrawRef.current.disable();
                                    setIsDrawingRadioMetricTarget(false);
                                } else {
                                    onRadioMetricTargetDrawRef.current.enable();
                                    setIsDrawingRadioMetricTarget(true);
                                    setIsDrawingSoil(false);
                                }
                            }
                        }}
                    >
                        {openDrawer ? (
                            <BorderVerticalIcon />
                        ) : (
                            <>
                                {isDrawingRadioMetricTarget
                                    ? "Stop Drawing"
                                    : "Create Radiometric Target"}
                                <AddIcon />
                            </>
                        )}
                    </Fab>
                </>
            )}

            <ParcellaireHCCSaveModal
                open={isHCCSaveOpen}
                setIsOpen={setIsHCCSaveOpen}
                parcellaireState={parcellaireState}
                parcellaireDispatch={parcellaireDispatch}
                setGeoJsonKey={setGeoJsonKey}
                elapsedSeconds={elapsedSeconds}
                resetTimer={() => setElapsedSeconds(0)}
                initialCompany={company}
                initialContract={contract}
                initialSite={site}
                initialMission={mission}
                orthoimageCompany={orthoimageCompany}
                orthoimageContract={orthoimageContract}
                orthoimageSite={orthoimageSite}
                orthoimageMission={orthoimageMission}
            />
            {parcellaireState.selectedLayers.length !== 0 && (
                <>
                    <Tooltip
                        title="Add property to selection"
                        placement="right"
                    >
                        <Fab
                            size="medium"
                            color="primary"
                            sx={(theme) => ({
                                position: "absolute",
                                left: 70,
                                bottom: 10,
                                zIndex: theme.zIndex.drawer,
                            })}
                            onClick={() => {
                                if (map) {
                                    map.pm.disableGlobalRotateMode();
                                }
                                setOpenAddPropertyDialog(true);
                            }}
                        >
                            <AddIcon />
                        </Fab>
                    </Tooltip>
                    <Tooltip title="Scale selected plots" placement="right">
                        <Fab
                            size="medium"
                            color="primary"
                            sx={(theme) => ({
                                position: "absolute",
                                left: 70,
                                bottom: 65,
                                zIndex: theme.zIndex.drawer,
                            })}
                            onClick={() => {
                                if (map) {
                                    map.pm.disableGlobalRotateMode();
                                }
                                setOpenScaleDialog(true);
                            }}
                        >
                            <AspectRatioIcon />
                        </Fab>
                    </Tooltip>
                </>
            )}

            {isLiteral ? (
                <Dialog open={openExportForm} fullWidth maxWidth="xs">
                    <ParcellaireMetadataForm
                        parcellaireState={parcellaireState}
                        setIsOpen={setOpenExportForm}
                    />
                </Dialog>
            ) : (
                <Dialog open={openOrthoForm} fullWidth maxWidth="xs">
                    <ParcellaireOrthoForm
                        setIsOpen={setOpenOrthoForm}
                        parcellaireDispatch={parcellaireDispatch}
                        setGeoJsonKey={setGeoJsonKey}
                        initialCompany={company}
                        initialContract={contract}
                        initialSite={site}
                        initialMission={mission}
                        orthoimageCompany={orthoimageCompany}
                        orthoimageContract={orthoimageContract}
                        orthoimageSite={orthoimageSite}
                        orthoimageMission={orthoimageMission}
                        fastOrthoimage={fastOrthoimage}
                        setSearchParams={setSearchParams}
                    />
                </Dialog>
            )}

            <Dialog open={openAddPropertyDialog} fullWidth maxWidth="xs">
                <ParcellaireAddProperty
                    open={openAddPropertyDialog}
                    onClose={() => setOpenAddPropertyDialog(false)}
                    parcellaireState={parcellaireState}
                    parcellaireDispatch={parcellaireDispatch}
                    setGeoJsonKey={setGeoJsonKey}
                    setSelectableMetadata={setSelectableMetadata}
                />
            </Dialog>

            <Dialog open={openScaleDialog} fullWidth maxWidth="xs">
                <ParcellaireScalePlots
                    onClose={() => setOpenScaleDialog(false)}
                    parcellaireState={parcellaireState}
                    parcellaireDispatch={parcellaireDispatch}
                    setGeoJsonKey={setGeoJsonKey}
                />
            </Dialog>
        </FormProvider>
    );
}

const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "center",
}));

const getParcellaireFormDefaultValues = (isLiteral) =>
    isLiteral
        ? {
              xCount: 20,
              yCount: 4,
              latitude: null,
              longitude: null,
              xSize: 3,
              ySize: 1,
              xSpacing: 0.5,
              ySpacing: 0.2,
              angle: 0,
              selectedMetadata: {
                  label: "X and Y",
                  id: DYNAMIC_FEATURE_X_Y_FIELD_NAME,
              },
          }
        : {
              xCount: "",
              yCount: "",
              latitude: null,
              longitude: null,
              xSize: "",
              ySize: "",
              xSpacing: "",
              ySpacing: "",
              angle: 0,
              selectedMetadata: null,
          };
